// A channel between two browsers looking at the same meeting.
//
// The screen the prospect watches and the advisor's iPad pass each other small
// messages as they happen: which page is showing, the line being drawn, the
// finished stroke. This is the one place that knows how those messages travel,
// so the meeting is written against `send` and `onMessage` and nothing else.
//
// They travel one of two ways.
//
// On a deployed site, through Azure Web PubSub. The page asks the app's own
// API for a connection (`/api/LiveNegotiate`, which only answers someone who
// is signed in), is given a socket address good for this pairing's group and
// nothing else, and from then on the two devices publish to that group
// directly. Nothing is stored there; a message goes to whoever is connected
// and is gone. A build uses the service only when it is made with
// VITE_LIVE=webpubsub, which each environment's deploy workflow sets once the
// environment has one (the dev workflow does). A build without it never asks,
// and the meeting does not offer to connect an iPad. Even with it, the API is
// asked first whether it has a live service, and its word is final.
//
// On a developer's machine, through the dev server's own relay
// (vite.live-relay.js), over the socket Vite already keeps open, so nothing in
// Azure is needed to work on this. Setting VITE_LIVE=webpubsub makes a dev
// server use the real service instead.

const EVENT = 'pq:live';
const NEGOTIATE = '/api/LiveNegotiate';
const SUBPROTOCOL = 'json.webpubsub.azure.v1';
const hot = import.meta.hot;
const forced = import.meta.env.VITE_LIVE || '';
const relay = Boolean(hot) && forced !== 'webpubsub';

/** How messages travel in this build, or null when they cannot. */
export const LIVE_TRANSPORT = relay ? 'dev-relay' : forced === 'webpubsub' ? 'webpubsub' : null;

/**
 * Whether this environment can link two devices right now. The relay always
 * can. The real service can when the API says it has one.
 */
export async function liveAvailable(fetcher = globalThis.fetch, transport = LIVE_TRANSPORT) {
  if (transport === 'dev-relay') return true;
  if (transport !== 'webpubsub') return false;
  try {
    const res = await fetcher(NEGOTIATE, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return false;
    const body = await res.json();
    return body?.available === true;
  } catch {
    return false;
  }
}

const newId = () => Math.random().toString(36).slice(2, 10);

function openRelay(room, onMessage) {
  const id = newId();
  let open = true;
  const handler = msg => {
    if (open && msg && msg.room === room && msg.from !== id) onMessage(msg);
  };
  hot.on(EVENT, handler);
  return {
    id,
    send(type, body = {}) {
      if (open) hot.send(EVENT, { ...body, room, from: id, type });
    },
    close() {
      open = false;
      if (typeof hot.off === 'function') hot.off(EVENT, handler);
    },
  };
}

/** How long to wait before the nth attempt to connect again: half a second, doubling, to fifteen. */
export function retryDelay(attempt) {
  return Math.min(15000, 500 * 2 ** Math.min(Math.max(attempt, 1) - 1, 5));
}

/**
 * A member of a pairing's group on the real service.
 *
 * The connection is asked for, opened, and opened again whenever it ends: a
 * token runs out after a couple of hours, and an iPad's Wi-Fi comes and goes.
 * A message sent while it is down is dropped rather than queued, because the
 * meeting above this already repeats what matters (a heartbeat, the strokes
 * the screen has not acknowledged, the whole state when the two differ), and
 * a queue of stale page turns arriving late would be worse than none.
 *
 * `deps` lets a test stand in for the network.
 */
export function openPubSub(room, onMessage, deps = {}) {
  const fetcher = deps.fetch || globalThis.fetch;
  const Socket = deps.WebSocket || globalThis.WebSocket;
  const later = deps.setTimeout || globalThis.setTimeout;
  const cancel = deps.clearTimeout || globalThis.clearTimeout;

  const id = newId();
  let socket = null;
  let group = `pair-${room}`;
  let closed = false;
  let attempt = 0;
  let timer = null;

  const again = () => {
    if (closed) return;
    attempt += 1;
    cancel(timer);
    timer = later(connect, retryDelay(attempt));
  };

  async function connect() {
    if (closed) return;
    let url = '';
    try {
      const res = await fetcher(`${NEGOTIATE}?room=${encodeURIComponent(room)}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const body = res.ok ? await res.json() : null;
      url = body?.url || '';
      if (body?.group) group = body.group;
    } catch { /* tried again below */ }
    if (closed) return;
    if (!url) { again(); return; }

    const ws = new Socket(url, SUBPROTOCOL);
    socket = ws;
    ws.onopen = () => { attempt = 0; };
    ws.onmessage = ev => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg?.type === 'message' && msg.from === 'group' && msg.data && msg.data.from !== id) onMessage(msg.data);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* already closing */ } };
    ws.onclose = () => {
      if (socket === ws) socket = null;
      again();
    };
  }

  connect();

  return {
    id,
    send(type, body = {}) {
      if (closed || !socket || socket.readyState !== 1) return;
      socket.send(JSON.stringify({
        type: 'sendToGroup',
        group,
        noEcho: true,
        dataType: 'json',
        data: { ...body, from: id, type },
      }));
    },
    close() {
      closed = true;
      cancel(timer);
      const ws = socket;
      socket = null;
      if (ws) { try { ws.close(); } catch { /* already closed */ } }
    },
  };
}

/**
 * Join a room. Every message another member sends arrives at `onMessage` as
 * `{ type, from, ... }`; this member's own are not echoed back.
 */
export function openChannel(room, onMessage) {
  if (!room) return null;
  if (LIVE_TRANSPORT === 'dev-relay') return openRelay(room, onMessage);
  if (LIVE_TRANSPORT === 'webpubsub') return openPubSub(room, onMessage);
  return null;
}
